import { useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { SquareArrowUp, PlusSquare } from 'lucide-react';

import usePwaPrompt from '@/shared/hooks/usePwaPrompt.js';
import Button from '@/shared/components/elements/Button.jsx';
import Logo from '@/shared/components/modules/Logo.jsx';
import logo from '@/assets/images/Logo.png';
import whiteLogo from '@/assets/images/WhiteLogo.png';

const instructionSteps = [
  {
    text: (
      <>
        در نوار پایین مرورگر، روی آیکون{' '}
        <strong className="inline-flex items-center gap-1.5 font-semibold">
          <SquareArrowUp
            size={16}
            strokeWidth={2}
            className="text-[var(--color-primary-100)]"
          />
          <span>Share</span>
        </strong>{' '}
        ضربه بزنید.
      </>
    ),
  },
  {
    text: (
      <>
        گزینه{' '}
        <strong className="inline-flex items-center gap-1.5 font-semibold">
          <PlusSquare
            size={16}
            strokeWidth={2}
            className="text-[var(--color-primary-100)]"
          />
          <span>Add to Home Screen</span>
        </strong>{' '}
        را انتخاب کنید.
      </>
    ),
  },
  {
    text: (
      <>
        در پنجره باز شده، روی دکمه{' '}
        <strong className="font-semibold">Add</strong> در بالا ضربه بزنید.
      </>
    ),
  },
];

function InstructionStep({ text, stepNumber }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-20)] text-lg font-bold text-[var(--color-primary-100)] dark:bg-gray-700 dark:text-orange-400">
        {stepNumber}
      </div>
      <div className="flex-1 pt-0.5">
        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {text}
        </p>
      </div>
    </div>
  );
}

export default function IOSPwaPrompt() {
  const { showPrompt, dismissPrompt } = usePwaPrompt();

  useEffect(() => {
    if (showPrompt) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showPrompt]);

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const sheetVariants = {
    hidden: { y: '100%' },
    visible: {
      y: 0,
      transition: { type: 'spring', damping: 25, stiffness: 180 },
    },
    exit: { y: '100%', transition: { type: 'tween', duration: 0.25 } },
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={dismissPrompt}
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
            aria-hidden="true"
          />

          <motion.div
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.3 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 600) {
                dismissPrompt();
              }
            }}
            className="fixed right-0 bottom-0 left-0 z-[9999] flex touch-none flex-col items-center rounded-t-3xl bg-white/95 p-5 pb-8 shadow-2xl shadow-black/20 backdrop-blur-xl select-none dark:bg-[#1c1f36]/95 dark:shadow-[0_-10px_40px_rgba(0,0,0,0.5)]"
          >
            <div className="mb-4 h-1.5 w-12 rounded-full bg-gray-300 dark:bg-gray-700" />

            <div className="mb-5 flex w-full flex-col items-center">
              {/* Logo */}
              <div className="mb-6">
                <Logo
                  src={logo}
                  darkSrc={whiteLogo}
                  alt="لوگو"
                  imageClassName="h-10 w-auto object-contain"
                  draggable="false"
                />
              </div>
            </div>

            <p className="mb-5 max-w-sm text-center text-base leading-relaxed text-gray-700 dark:text-gray-300">
              برای دسترسی سریع‌تر، اینتلیو را به صفحه اصلی دستگاه خود اضافه
              کنید.
            </p>

            <div className="w-full max-w-sm space-y-4 rounded-2xl border border-black/5 bg-gray-50/80 p-4 dark:border-white/10 dark:bg-white/5">
              {instructionSteps.map((step, index) => (
                <InstructionStep
                  key={index}
                  stepNumber={index + 1}
                  text={step.text}
                />
              ))}
            </div>

            <div className="mt-6 w-full max-w-sm">
              <Button
                variant="primary"
                size="xl"
                fullWidth={true}
                onClick={dismissPrompt}
                className="text-base font-bold shadow-[0_8px_20px_rgba(239,89,38,0.3)]"
              >
                متوجه شدم
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
